import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { ArrowRight, Menu, X } from "lucide-react";
import { BrandMark } from "@/components/layout/Sidebar";
import { cn } from "@/lib/utils";
import { usePublicConfig } from "@/hooks/queries/usePublicConfig";
import { usePublicPlans } from "@/hooks/queries/usePublicPlans";
import { HeroMock } from "@/components/landing/HeroMock";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { Differentiators } from "@/components/landing/Differentiators";
import { Comparison } from "@/components/landing/Comparison";
import { Pricing } from "@/components/landing/Pricing";
import { LandingFaq } from "@/components/landing/LandingFaq";
import { SECTION_CLASS } from "@/components/landing/SectionHeading";

/** Touch devices: lift buttons, tabs, inputs and links in this page to a 40px tap target. */
const TOUCH_TARGETS = "pointer-coarse:[&_button:not([role=switch]):not([role=checkbox])]:min-h-10 pointer-coarse:[&_button:not([role=switch]):not([role=checkbox])]:min-w-10 pointer-coarse:[&_input:not([type=checkbox]):not([type=radio])]:min-h-10 pointer-coarse:[&_a[href]]:min-h-10";

/* ---------------- Purely presentational content ---------------- */

const NAV_LINKS = [
  { href: "#how-it-works", label: "How it works" },
  { href: "#features", label: "Features" },
  { href: "#compare", label: "Compare" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const PROVIDERS = ["OpenAI", "Anthropic", "Google Gemini", "AWS Bedrock", "Azure OpenAI", "Mistral", "Cohere", "DeepSeek", "xAI", "Groq", "OpenRouter"];

const SALES_MAILTO = "mailto:sales@genaicore.com";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

const navLinkClass = "pointer-coarse:inline-flex pointer-coarse:items-center text-sm text-foreground/75 hover:text-foreground transition-colors";
const footerLinkClass = "text-[13px] text-muted-foreground hover:text-foreground transition-colors";

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, isLoading } = useAuth();
  const { data: publicConfig } = usePublicConfig();
  const { plans } = usePublicPlans();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLoginClick = () => navigate("/login");
  const handleDashboardClick = () => navigate("/");

  // Sign-ups can be switched off by the operator; fall back to the sign-in page.
  const signUpPath = publicConfig?.allowRegistration === false ? "/login" : "/register";
  const handleStartClick = () => navigate(isAuthenticated ? "/" : signUpPath);
  const startLabel = isAuthenticated ? "Go to Dashboard" : "Start free";

  const toggleMobileMenu = () => setMobileMenuOpen((open) => !open);

  return (
    <div className={`min-h-screen bg-canvas text-foreground ${TOUCH_TARGETS}`}>
      {/* ---------------- Navigation ---------------- */}
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-colors duration-300",
          scrolled || mobileMenuOpen ? "bg-canvas/85 backdrop-blur-md border-b border-border/60" : "border-b border-transparent"
        )}
      >
        <div className="mx-auto max-w-6xl h-16 px-4 sm:px-6 flex items-center justify-between gap-6">
          <BrandMark to="/" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {NAV_LINKS.map((l) => (
              <a key={l.href} className={navLinkClass} href={l.href}>
                {l.label}
              </a>
            ))}
            <Link className={navLinkClass} to="/docs">Docs</Link>
          </div>

          <div className="hidden md:flex items-center gap-2">
            {isLoading ? (
              <div className="h-10 w-24 bg-surface-muted animate-pulse rounded-full" />
            ) : isAuthenticated ? (
              <span className="text-[13px] text-muted-foreground hidden lg:inline-block mr-1">
                Welcome, <span className="text-foreground">{user?.username || 'User'}</span>
              </span>
            ) : (
              <Button variant="ghost" className="hidden sm:flex rounded-full h-10 px-4" onClick={handleLoginClick}>
                Log in
              </Button>
            )}
            {isAuthenticated ? (
              <Button className="rounded-full h-10 px-5" onClick={handleDashboardClick}>
                Go to Dashboard
              </Button>
            ) : (
              <Button className="rounded-full h-10 px-5" asChild>
                <a href={SALES_MAILTO}>Talk to sales</a>
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-border bg-card hover:bg-surface-muted transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" strokeWidth={1.8} /> : <Menu className="h-5 w-5" strokeWidth={1.8} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden px-4 pb-4">
            <div className="rounded-card bg-card p-4 space-y-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="block rounded-xl px-3 py-2.5 text-[15px] hover:bg-surface-muted transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {l.label}
                </a>
              ))}
              <Link to="/docs" className="block rounded-xl px-3 py-2.5 text-[15px] hover:bg-surface-muted transition-colors">
                Docs
              </Link>

              <div className="pt-3 mt-2 border-t border-border/60 flex flex-col gap-2">
                {isAuthenticated ? (
                  <>
                    <span className="px-3 pt-1 text-[13px] text-muted-foreground">
                      Welcome, <span className="text-foreground">{user?.username || 'User'}</span>
                    </span>
                    <Button
                      className="rounded-full h-11 w-full"
                      onClick={() => {
                        handleDashboardClick();
                        setMobileMenuOpen(false);
                      }}
                    >
                      Go to Dashboard
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      className="justify-start rounded-xl h-11 px-3"
                      onClick={() => {
                        handleLoginClick();
                        setMobileMenuOpen(false);
                      }}
                    >
                      Log in
                    </Button>
                    <Button className="rounded-full h-11 w-full" asChild>
                      <a href={SALES_MAILTO}>Talk to sales</a>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* ---------------- Hero ---------------- */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-32 sm:pt-40 pb-16 sm:pb-24">
          <div className="max-w-4xl">
            <motion.p {...fadeUp(0)} className="inline-flex items-center gap-2 h-8 rounded-full border border-border bg-card px-3 text-xs font-medium text-foreground/80">
              <span className="h-2 w-2 rounded-full bg-brand" />
              For agencies and SaaS builders
            </motion.p>

            <motion.h1
              {...fadeUp(0.1)}
              className="mt-6 text-[44px] sm:text-[64px] lg:text-[76px] font-medium tracking-[-0.04em] leading-[0.98]"
            >
              The AI backend for agencies.
              <span className="block text-subtle">Build once. Resell to every client.</span>
            </motion.h1>

            <motion.p {...fadeUp(0.2)} className="mt-7 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Build chatbots, document Q&amp;A and agents once on hosted knowledge bases, agents and workflows, and call them as a
              model name from the OpenAI SDK on any of 20+ providers, with your own provider keys or ours. Each client gets an
              account with its own wallet, API key and usage, and your markup is credited to you on every request.
            </motion.p>

            <motion.div {...fadeUp(0.3)} className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button size="lg" className="rounded-full h-12 pl-6 pr-5 text-sm gap-3" onClick={handleStartClick}>
                {startLabel}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-6 text-sm" asChild>
                <Link to="/docs">Read the docs</Link>
              </Button>
            </motion.div>
          </div>

          {/* Product showcase */}
          <motion.div
            className="mt-16 sm:mt-20"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
          >
            <HeroMock />
          </motion.div>
        </section>

        {/* ---------------- Provider strip ---------------- */}
        <section id="providers" className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 sm:pb-24 scroll-mt-20">
          <h2 className="text-[13px] text-muted-foreground mb-5">One API for 20+ providers, with your keys or ours</h2>
          <ul className="flex flex-wrap gap-2">
            {PROVIDERS.map((name) => (
              <li key={name} className="inline-flex items-center gap-2.5 h-11 rounded-full bg-card pl-1.5 pr-4 text-sm font-medium">
                <span className="h-8 w-8 rounded-full bg-surface-muted flex items-center justify-center text-[11px] font-semibold text-foreground/80" aria-hidden>
                  {name.slice(0, 2)}
                </span>
                {name}
              </li>
            ))}
          </ul>
        </section>

        <HowItWorks />
        <Differentiators />
        <Comparison />
        <Pricing data={plans} onStart={handleStartClick} startLabel={startLabel} />
        <LandingFaq data={plans} />

        {/* ---------------- CTA ---------------- */}
        <section id="get-started" className={SECTION_CLASS}>
          <div className="relative overflow-hidden rounded-card bg-card p-8 sm:p-12 md:p-16">
            <div className="absolute right-0 top-0 bottom-0 hidden md:flex items-end gap-2 p-8 w-[38%]" aria-hidden>
              {[30, 46, 38, 62, 54, 78, 92].map((h, i, arr) => (
                <span key={i} className={cn("flex-1 rounded-[10px]", i === arr.length - 1 ? "bg-brand" : i % 2 ? "bg-stripes-brand" : "bg-surface-muted")} style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="relative max-w-xl">
              <h2 className="text-[30px] sm:text-[40px] font-medium tracking-[-0.03em] leading-[1.08]">
                Build it once.
                <span className="block text-subtle">Sell it to every client.</span>
              </h2>
              <p className="text-[15px] sm:text-base text-muted-foreground mt-4">
                Start on the free Developer plan, build your first knowledge base or agent, and upgrade to an Agency plan when you
                add clients.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button size="lg" className="rounded-full h-12 pl-6 pr-5 text-sm gap-3" onClick={handleStartClick}>
                  {startLabel}
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full h-12 px-6 text-sm" asChild>
                  <a href={SALES_MAILTO}>Talk to sales</a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ---------------- Footer ---------------- */}
      <footer className="mx-auto max-w-6xl px-4 sm:px-6 pb-10 pt-6">
        <div className="rounded-card bg-card p-6 sm:p-10">
          <div className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-8">
            <div className="col-span-2 md:col-span-1">
              <BrandMark to="/" />
              <p className="text-[13px] text-muted-foreground mt-4 max-w-xs">The AI backend for agencies and SaaS builders. Build once, resell to every client, on any model.</p>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-4">Platform</h3>
              <ul className="space-y-2.5">
                <li><a href="#how-it-works" className={footerLinkClass}>How it works</a></li>
                <li><a href="#features" className={footerLinkClass}>Features</a></li>
                <li><a href="#compare" className={footerLinkClass}>Compare</a></li>
                <li><a href="#pricing" className={footerLinkClass}>Pricing</a></li>
                <li><a href="#faq" className={footerLinkClass}>FAQ</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-4">Documentation</h3>
              <ul className="space-y-2.5">
                <li><Link to="/docs" className={footerLinkClass}>Documentation Hub</Link></li>
                <li><Link to="/docs/get-started/quickstart" className={footerLinkClass}>Quickstart</Link></li>
                <li><Link to="/docs/get-started/agencies" className={footerLinkClass}>For agencies</Link></li>
                <li><Link to="/docs/production/cost-management" className={footerLinkClass}>Pricing &amp; billing</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-4">Legal</h3>
              <ul className="space-y-2.5">
                <li><a href="#" className={footerLinkClass}>Terms of Service</a></li>
                <li><a href="#" className={footerLinkClass}>Privacy Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/60 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground text-center md:text-left">
              &copy; {new Date().getFullYear()} GenAICore. All rights reserved.
            </p>
            <a href={SALES_MAILTO} className={footerLinkClass}>sales@genaicore.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
